import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ModalShell } from '../lib/ui';
import { isSplitSlot, halfLabel } from '../lib/trayUtils';

const ipcRenderer = window.process?.type === 'renderer' ? window.require('electron').ipcRenderer : null;

const LAYOUT_URL = 'https://teardrophydro.co.uk/garageFarm/bench-layout';
const ORDERS_API_BASE = 'https://teardrophydro.co.uk/garageFarm/orders';

function authHeaders() {
  const token = localStorage.getItem('accessToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// "Coriander 50g" → { product: "Coriander", weight: "50g" }
function parseItemName(name) {
  const m = name.match(/^(.*?)\s+(\d+(?:\.\d+)?(?:g|kg|ml|l))$/i);
  return m ? { product: m[1], weight: m[2] } : { product: name, weight: '' };
}

// "The Simple Salad Bag (~200g mixed)" → "Simple Salad Bag"
function cleanBoxName(name) {
  return name
    .replace(/\s*\(.*?\)\s*/g, '')
    .replace(/^The\s+/i, '')
    .trim();
}

function looksLikeBox(name) {
  return /bag|box|mix|bundle|combo|collection/i.test(name);
}

// Flatten layout benches into a list of { benchName, layerIndex, trayIndex, half?, tray }
// Split slots (half-size micro trays) hold two independent halves, each a
// separate selectable tray - see trayUtils.isSplitSlot.
function extractTrays(layout) {
  if (!layout?.benches) return [];
  const out = [];
  Object.entries(layout.benches).forEach(([benchName, bench]) => {
    bench.layers.forEach((layer, layerIndex) => {
      layer.forEach((slot, trayIndex) => {
        if (isSplitSlot(slot)) {
          (slot.halves || []).forEach((tray, half) => {
            if (tray?.cropType) out.push({ benchName, layerIndex, trayIndex, half, tray });
          });
        } else if (slot?.cropType) {
          out.push({ benchName, layerIndex, trayIndex, tray: slot });
        }
      });
    });
  });
  return out;
}

function cropMatches(tray, product) {
  const a = (tray.cropType || '').toLowerCase().trim();
  const b = product.toLowerCase().trim();
  return a.includes(b) || b.includes(a);
}

const labelStyle = {
  fontSize: '10px', color: 'var(--muted)', fontWeight: 700,
  textTransform: 'uppercase', letterSpacing: '0.05em',
  display: 'block', marginBottom: 4,
};

function computeUseBy(daysFromNow) {
  const d = new Date();
  d.setDate(d.getDate() + daysFromNow);
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

const STORAGE_PRESETS = ['Keep refrigerated', 'Store at room temperature'];

const SLOT_LABELS = { morning: 'Morning', evening: 'Evening', late: 'Late' };

// Whether to print the customer name/address label is remembered between
// orders, so once it's switched on it stays on for the day's packing.
const ADDRESS_PREF_KEY = 'printAddressLabel';
function readAddressPref() {
  try { return localStorage.getItem(ADDRESS_PREF_KEY) !== 'false'; } catch { return true; }
}
function writeAddressPref(on) {
  try { localStorage.setItem(ADDRESS_PREF_KEY, on ? 'true' : 'false'); } catch {}
}

export default function PrintOrderModal({ order, onClose, onTraceabilitySaved }) {

  const [allTrays,     setAllTrays]     = useState([]);
  const [loadingTrays, setLoadingTrays] = useState(true);
  const [status,       setStatus]       = useState('');
  const [printing,     setPrinting]     = useState(false);
  const [done,         setDone]         = useState(false);
  const [error,        setError]        = useState('');

  const [addressOn,      setAddressOn]      = useState(readAddressPref);
  const [addressCopies,  setAddressCopies]  = useState(1);
  const [addressPreview, setAddressPreview] = useState(null);
  const [addressPreviewing, setAddressPreviewing] = useState(false);

  const addressLabelData = {
    kind:    'address',
    name:    order.name || '',
    address: order.address || {},
    slot:    SLOT_LABELS[order.deliverySlot] || '',
  };
  const addressSummary = [order.address?.line1, order.address?.city, order.address?.postcode].filter(Boolean).join(', ');

  const [items, setItems] = useState(() =>
    order.items
      .filter(i => i.name !== 'Local delivery')
      .map(i => {
        const isBox = looksLikeBox(i.name);
        const { product, weight } = parseItemName(i.name);
        return {
          name:         i.name,
          displayName:  isBox ? cleanBoxName(i.name) : product,
          weight:       isBox ? '' : weight,
          qty:          i.quantity,
          isBox,
          included:     true,
          selectedTray: null,
          allergen:     'alongside',
          shelfLife:    7,
          storage:      'Keep refrigerated',
          preview:      null,
          previewing:   false,
          pickerOpen:   false,
          traySearch:   '',
        };
      })
  );

  // Fetch farm layout so we can show trays for selection
  useEffect(() => {
    axios.get(LAYOUT_URL, { headers: authHeaders() })
      .then(res => setAllTrays(extractTrays(res.data)))
      .catch(err => console.error('Tray layout fetch failed', err))
      .finally(() => setLoadingTrays(false));
  }, []);

  // Progress messages from the main process during printing
  useEffect(() => {
    if (!ipcRenderer) return;
    const handler = (_, msg) => setStatus(msg);
    ipcRenderer.on('print-progress', handler);
    return () => ipcRenderer.removeListener('print-progress', handler);
  }, []);

  const update = (idx, fields) =>
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, ...fields } : it));

  const totalLabels = items.filter(it => it.included).reduce((n, it) => n + it.qty, 0)
    + (addressOn ? addressCopies : 0);

  // Tray/bench chosen here for the label doubles as the order's traceability
  // record, so printing captures tracking info in one step instead of two.
  const buildTraceabilityRows = () => {
    const existing = order.traceability || [];
    return order.items
      .filter(i => i.name !== 'Local delivery')
      .map(orderItem => {
        const prior = existing.find(t => t.itemName === orderItem.name) || {};
        const printItem = items.find(it => it.name === orderItem.name);
        const sel = printItem?.included ? printItem?.selectedTray : null;
        if (sel && sel.benchName) {
          const trayLabel = `L${sel.layerIndex + 1} T${sel.trayIndex + 1}${sel.half !== undefined ? halfLabel(sel.half) : ''}`;
          return { itemName: orderItem.name, tray: trayLabel, bench: sel.benchName, notes: prior.notes || '' };
        }
        return { itemName: orderItem.name, tray: prior.tray || '', bench: prior.bench || '', notes: prior.notes || '' };
      });
  };

  const saveTraceability = async () => {
    // Subscription placeholders have no Order doc yet - nothing to save against.
    if (order.isSubPlaceholder) return;
    try {
      const rows = buildTraceabilityRows();
      await axios.patch(`${ORDERS_API_BASE}/${order._id}/traceability`,
        { traceability: rows },
        { headers: authHeaders() }
      );
      onTraceabilitySaved?.(order._id, rows);
    } catch (e) { console.error('Traceability autosave failed', e); }
  };

  const handlePreview = async (idx) => {
    const it = items[idx];
    const t  = it.selectedTray?.tray;
    const batchId = it.isBox ? null : (t?.batchId || order._id);
    const labelData = {
      company:  'Teardrop',
      product:  it.displayName,
      weight:   it.weight,
      useBy:    computeUseBy(it.shelfLife),
      batchId,
      allergen: it.allergen,
      storage:  it.storage,
    };
    update(idx, { previewing: true, preview: null });
    const result = await ipcRenderer.invoke('preview-label', labelData);
    update(idx, { previewing: false, preview: result.success ? result.dataUrl : null });
  };

  const handleAddressPreview = async () => {
    setAddressPreviewing(true); setAddressPreview(null);
    const result = await ipcRenderer.invoke('preview-label', addressLabelData);
    setAddressPreviewing(false);
    setAddressPreview(result.success ? result.dataUrl : null);
  };

  const handlePrint = async () => {
    setError(''); setPrinting(true); setDone(false);

    await saveTraceability();

    // Customer label comes out first, so it heads this order's strip of labels.
    const addressLabels = addressOn
      ? Array.from({ length: addressCopies }, () => addressLabelData)
      : [];

    const productLabels = items.filter(it => it.included).flatMap(it => {
      const t = it.selectedTray?.tray;
      const batchId = it.isBox ? null : (t?.batchId || order._id);

      return Array.from({ length: it.qty }, () => ({
        company:  'Teardrop',
        product:  it.displayName,
        weight:   it.weight,
        useBy:    computeUseBy(it.shelfLife),
        batchId,
        allergen: it.allergen,
        storage:  it.storage,
      }));
    });
    const labelsData = [...addressLabels, ...productLabels];

    if (!ipcRenderer) {
      setPrinting(false);
      setError('Label printing is only available in the desktop app.');
      return;
    }
    const result = await ipcRenderer.invoke('print-labels', labelsData);
    setPrinting(false);
    if (result.success) setDone(true);
    else setError(result.error || 'Unknown error');
  };

  return (
    <ModalShell onClose={onClose} width={1240} maxWidth="96vw" ariaLabel="Print Labels">
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 18 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: 'var(--text)' }}>Print Labels</h2>
            <p style={{ margin: '3px 0 0', fontSize: '12px', color: 'var(--muted)' }}>
              {order.name} · {order.isSubPlaceholder ? 'Subscription delivery due' : `Order ${order._id}`}
            </p>
            <p style={{ margin: '4px 0 0', fontSize: '11px', color: 'var(--muted)' }}>
              {order.isSubPlaceholder
                ? 'Labels only - tray picks are not saved until the delivery is charged or logged.'
                : 'Tray/bench picks below are saved as this order\'s tracking when you print - no separate entry needed.'}
            </p>
          </div>
          <button onClick={onClose} style={{
            background: 'var(--panel-2)', border: 'none', borderRadius: 'var(--r-md)',
            width: 28, height: 28, cursor: 'pointer', fontSize: '14px', color: 'var(--muted)',
          }}>✕</button>
        </div>

        {/* Customer name + address label for the bag */}
        <div style={{
          background: addressOn ? 'var(--panel-2)' : 'var(--bg)',
          border: '1px solid var(--border)', borderRadius: 'var(--r-lg)',
          padding: '12px', marginBottom: 10, opacity: addressOn ? 1 : 0.55,
          transition: 'opacity .15s, background .15s',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={addressOn}
                onChange={e => { setAddressOn(e.target.checked); writeAddressPref(e.target.checked); }}
                style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--brand-2)' }}
              />
              <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text)' }}>Customer label for the bag</span>
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {addressOn && (
                <>
                  <span style={{ fontSize: '11px', color: 'var(--muted)' }}>Copies</span>
                  <input
                    type="number" min={1} max={10} value={addressCopies}
                    onChange={e => setAddressCopies(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                    style={{
                      width: 44, padding: '3px 6px', fontSize: '12px', textAlign: 'center',
                      border: '1px solid var(--border)', borderRadius: 'var(--r-md)', color: 'var(--text)',
                    }}
                  />
                </>
              )}
              {ipcRenderer && addressOn && (
                <button
                  onClick={handleAddressPreview}
                  disabled={addressPreviewing}
                  style={{
                    fontSize: '11px', padding: '3px 9px', borderRadius: 'var(--r-md)', cursor: 'pointer',
                    border: '1px solid var(--border)', background: 'var(--panel-2)', color: 'var(--muted)',
                  }}
                >{addressPreviewing ? '…' : 'Preview'}</button>
              )}
            </div>
          </div>
          {addressOn && (
            <div style={{ fontSize: '12px', color: 'var(--muted)', marginTop: 6 }}>
              {order.name || 'No name'} · {addressSummary || 'no address on this order'}
              {addressLabelData.slot && ` · ${addressLabelData.slot}`}
              <span style={{ display: 'block', fontSize: '11px', marginTop: 2 }}>Prints first, before this order's product labels.</span>
            </div>
          )}
          {addressOn && addressPreview && (
            <img
              src={addressPreview}
              alt="Customer label preview"
              style={{
                width: 320, maxWidth: '100%', display: 'block', marginTop: 8,
                border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                imageRendering: 'pixelated', background: '#fff',
              }}
            />
          )}
        </div>

        {/* Per-item sections */}
        {items.map((item, idx) => {
          const matchingTrays = allTrays.filter(t => cropMatches(t, item.displayName));
          const sel = item.selectedTray;

          return (
            <div key={item.name} style={{
              background: item.included ? 'var(--panel-2)' : 'var(--bg)',
              border: `1px solid var(--border)`,
              borderRadius: 'var(--r-lg)', padding: '12px', marginBottom: 10,
              opacity: item.included ? 1 : 0.55,
              transition: 'opacity .15s, background .15s',
            }}>
              {/* Item header row */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: item.included ? 10 : 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  {/* Include toggle */}
                  <input
                    type="checkbox"
                    checked={item.included}
                    onChange={e => update(idx, { included: e.target.checked })}
                    style={{ width: 15, height: 15, cursor: 'pointer', accentColor: 'var(--brand-2)' }}
                    title={item.included ? 'Exclude from print' : 'Include in print'}
                  />
                  <span style={{ fontWeight: 700, fontSize: '14px', color: 'var(--text)' }}>{item.displayName}</span>
                  {item.weight && (
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>{item.weight}</span>
                  )}
                  <span style={{
                    background: 'var(--panel-2)', color: 'var(--brand-2)',
                    fontSize: '11px', fontWeight: 700, padding: '1px 7px', borderRadius: 'var(--r-sm)',
                  }}>×{item.qty}</span>
                  {item.isBox && (
                    <span style={{
                      background: '#f0fdf4', color: '#16a34a', border: '1px solid #86efac',
                      fontSize: '11px', fontWeight: 600, padding: '1px 7px', borderRadius: 'var(--r-sm)',
                    }}>Box - no batch needed</span>
                  )}
                </div>
                {/* Right side: preview button + box toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {ipcRenderer && item.included && (
                    <button
                      onClick={() => handlePreview(idx)}
                      disabled={item.previewing}
                      style={{
                        fontSize: '11px', padding: '3px 9px', borderRadius: 'var(--r-md)', cursor: 'pointer',
                        border: '1px solid var(--border)', background: 'var(--panel-2)', color: 'var(--muted)',
                      }}
                    >{item.previewing ? '…' : 'Preview'}</button>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '11px', color: 'var(--muted)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={item.isBox}
                      onChange={e => update(idx, { isBox: e.target.checked, selectedTray: null, pickerOpen: false, preview: null })}
                    />
                    is box
                  </label>
                </div>
              </div>

              {/* Tray picker and allergen - only shown when included */}
              {!item.isBox && item.included && (
                <div style={{ marginBottom: 10 }}>
                  <label style={labelStyle}>Select tray (sets batch ID)</label>

                  {sel ? (
                    /* Selected state */
                    <div style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 'var(--r-lg)', padding: '8px 10px',
                    }}>
                      <div>
                        <span style={{ fontWeight: 700, fontSize: '13px', color: '#15803d' }}>
                          {sel.tray.cropType}
                        </span>
                        <span style={{ fontSize: '11px', color: 'var(--muted)', marginLeft: 6 }}>
                          {sel.benchName} · L{sel.layerIndex + 1} T{sel.trayIndex + 1}{sel.half !== undefined ? halfLabel(sel.half) : ''}
                          {sel.tray.seededAt && ` · Seeded ${sel.tray.seededAt}`}
                        </span>
                        {sel.tray.batchId && (
                          <span style={{
                            marginLeft: 8, fontSize: '11px', fontWeight: 700,
                            color: 'var(--brand-2)', background: 'var(--panel-2)',
                            border: '1px solid var(--border)', borderRadius: 'var(--r-sm)', padding: '1px 6px',
                          }}>Batch: {sel.tray.batchId}</span>
                        )}
                      </div>
                      <button
                        onClick={() => update(idx, { selectedTray: null, pickerOpen: true, traySearch: '' })}
                        style={{
                          background: 'none', border: 'none', color: 'var(--muted)',
                          cursor: 'pointer', fontSize: '11px', textDecoration: 'underline',
                        }}
                      >Change</button>
                    </div>
                  ) : (
                    /* Picker trigger */
                    <button
                      onClick={() => update(idx, { pickerOpen: !item.pickerOpen, traySearch: '' })}
                      style={{
                        width: '100%', textAlign: 'left',
                        background: '#fff', border: '1px dashed var(--border)', borderRadius: 'var(--r-lg)',
                        padding: '8px 10px', fontSize: '12px', color: 'var(--muted)', cursor: 'pointer',
                      }}
                    >
                      {loadingTrays
                        ? 'Loading trays…'
                        : item.pickerOpen
                          ? '▲ Hide trays'
                          : `▼ Choose from farm  ${matchingTrays.length ? `(${matchingTrays.length} matching)` : '- no matches'}`
                      }
                    </button>
                  )}

                  {/* Tray list with search */}
                  {!sel && item.pickerOpen && (() => {
                    const q = item.traySearch.toLowerCase().trim();
                    const filtered = q
                      ? allTrays.filter(t =>
                          (t.tray.cropType  || '').toLowerCase().includes(q) ||
                          (t.benchName      || '').toLowerCase().includes(q) ||
                          (t.tray.batchId   || '').toLowerCase().includes(q)
                        )
                      : matchingTrays;

                    return (
                      <div style={{ marginTop: 4, border: '1px solid var(--border)', borderRadius: 'var(--r-lg)', background: '#fff' }}>
                        <div style={{ padding: '6px 8px', borderBottom: '1px solid var(--border)' }}>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Search by crop, bench or batch ID…"
                            value={item.traySearch}
                            onChange={e => update(idx, { traySearch: e.target.value })}
                            style={{
                              width: '100%', padding: '5px 8px', fontSize: '12px',
                              border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                              outline: 'none', boxSizing: 'border-box', color: 'var(--text)',
                            }}
                          />
                        </div>
                        <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                          {filtered.length === 0 ? (
                            <div style={{ padding: '12px', fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>
                              {q ? `No trays matching "${item.traySearch}"` : `No trays matching "${item.displayName}"`}
                              {!q && (
                                <button
                                  onClick={() => update(idx, { selectedTray: { tray: {}, benchName: '', layerIndex: 0, trayIndex: 0 }, pickerOpen: false })}
                                  style={{ display: 'block', marginTop: 6, fontSize: '11px', color: 'var(--brand-2)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                >
                                  Skip tray selection
                                </button>
                              )}
                            </div>
                          ) : (
                            filtered.map((t, ti) => {
                              const isMatch = !q && matchingTrays.includes(t);
                              return (
                                <div
                                  key={`${t.benchName}-${t.layerIndex}-${t.trayIndex}-${t.half ?? 'f'}`}
                                  onClick={() => update(idx, { selectedTray: t, pickerOpen: false, traySearch: '' })}
                                  style={{
                                    padding: '9px 12px', cursor: 'pointer',
                                    borderBottom: ti < filtered.length - 1 ? '1px solid var(--border)' : 'none',
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = '#f0fdf4'}
                                  onMouseLeave={e => e.currentTarget.style.background = ''}
                                >
                                  <div>
                                    <div style={{ fontWeight: 700, fontSize: '12px', color: 'var(--text)' }}>
                                      {t.tray.cropType}
                                      {isMatch && (
                                        <span style={{ marginLeft: 6, fontSize: '10px', fontWeight: 600, color: '#16a34a' }}>✓ match</span>
                                      )}
                                    </div>
                                    <div style={{ fontSize: '11px', color: 'var(--muted)', marginTop: 1 }}>
                                      {t.benchName} · L{t.layerIndex + 1} T{t.trayIndex + 1}{t.half !== undefined ? halfLabel(t.half) : ''}
                                      {t.tray.seededAt && ` · Seeded ${t.tray.seededAt}`}
                                    </div>
                                  </div>
                                  {t.tray.batchId ? (
                                    <span style={{
                                      fontSize: '11px', fontWeight: 700, color: 'var(--brand-2)',
                                      background: 'var(--panel-2)', border: '1px solid var(--border)',
                                      borderRadius: 'var(--r-sm)', padding: '2px 7px', whiteSpace: 'nowrap',
                                    }}>
                                      {t.tray.batchId}
                                    </span>
                                  ) : (
                                    <span style={{ fontSize: '11px', color: 'var(--muted)' }}>no batch ID</span>
                                  )}
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Shelf life + storage - only when included */}
              {item.included && (
                <div style={{ marginBottom: 10 }}>
                  {/* Shelf life row */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <span style={labelStyle}>Shelf life</span>
                    <input
                      type="number"
                      min={1}
                      max={30}
                      value={item.shelfLife}
                      onChange={e => update(idx, { shelfLife: Math.max(1, parseInt(e.target.value) || 1) })}
                      style={{
                        width: 44, padding: '3px 6px', fontSize: '12px', textAlign: 'center',
                        border: '1px solid var(--border)', borderRadius: 'var(--r-md)', color: 'var(--text)',
                      }}
                    />
                    <span style={{ fontSize: '12px', color: 'var(--muted)' }}>days</span>
                    <span style={{ fontSize: '12px', color: 'var(--muted)', marginLeft: 4 }}>
                      → Use by: <strong>{computeUseBy(item.shelfLife)}</strong>
                    </span>
                  </div>

                  {/* Storage instruction */}
                  <label style={labelStyle}>Storage</label>
                  <div style={{ display: 'flex', gap: 4, marginBottom: 5, flexWrap: 'wrap' }}>
                    {STORAGE_PRESETS.map(preset => (
                      <button
                        key={preset}
                        onClick={() => update(idx, { storage: preset })}
                        style={{
                          fontSize: '11px', padding: '3px 8px', borderRadius: 'var(--r-sm)',
                          cursor: 'pointer', border: '1px solid',
                          background: item.storage === preset ? 'rgba(46,125,50,0.12)' : 'var(--panel-2)',
                          borderColor: item.storage === preset ? 'var(--brand-2)' : 'var(--border)',
                          color: item.storage === preset ? 'var(--brand-2)' : 'var(--muted)',
                        }}
                      >{preset}</button>
                    ))}
                  </div>
                  <input
                    type="text"
                    value={item.storage}
                    onChange={e => update(idx, { storage: e.target.value })}
                    style={{
                      width: '100%', padding: '5px 8px', fontSize: '12px',
                      border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                      boxSizing: 'border-box', color: 'var(--text)',
                    }}
                  />
                </div>
              )}

              {/* Allergen toggle - only when included */}
              {item.included && <div>
                <label style={labelStyle}>Mustard allergen</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {['alongside', 'contains'].map(opt => (
                    <label key={opt} style={{
                      display: 'flex', alignItems: 'center', gap: 5,
                      fontSize: '12px', color: 'var(--text)', cursor: 'pointer',
                      background: item.allergen === opt ? 'rgba(46,125,50,0.12)' : 'var(--panel-2)',
                      border: `1px solid ${item.allergen === opt ? 'var(--brand-2)' : 'var(--border)'}`,
                      borderRadius: 'var(--r-sm)', padding: '4px 10px',
                    }}>
                      <input
                        type="radio"
                        name={`allergen-${idx}`}
                        value={opt}
                        checked={item.allergen === opt}
                        onChange={() => update(idx, { allergen: opt })}
                        style={{ margin: 0 }}
                      />
                      {opt === 'alongside' ? 'Grown alongside mustard' : 'Contains mustard'}
                    </label>
                  ))}
                </div>
              </div>}

            {/* Label preview image */}
            {item.preview && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={item.preview}
                  alt="Label preview"
                  style={{
                    width: '100%', display: 'block',
                    border: '1px solid var(--border)', borderRadius: 'var(--r-md)',
                    imageRendering: 'pixelated',
                    background: '#fff',
                  }}
                />
              </div>
            )}
            </div>
          );
        })}

        {/* Status / error */}
        {status && (
          <div style={{
            background: done ? '#f0fdf4' : '#f0f9ff',
            border: `1px solid ${done ? '#86efac' : '#bae6fd'}`,
            color: done ? '#166534' : '#0369a1',
            borderRadius: 'var(--r-lg)', padding: '8px 12px', fontSize: '12px', marginBottom: 12,
          }}>{status}</div>
        )}
        {error && (
          <div style={{
            background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626',
            borderRadius: 'var(--r-lg)', padding: '8px 12px', fontSize: '12px',
            whiteSpace: 'pre-wrap', marginBottom: 12,
          }}>{error}</div>
        )}

        {/* Footer */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{
              background: 'var(--panel-2)', border: 'none', borderRadius: 'var(--r-md)',
              padding: '8px 18px', fontSize: '13px', cursor: 'pointer',
              color: 'var(--muted)', fontWeight: 500,
            }}
          >{done ? 'Close' : 'Cancel'}</button>
          {!done && (
            <button
              onClick={handlePrint}
              disabled={printing}
              style={{
                background: printing ? 'var(--muted)' : 'var(--brand-2)',
                color: '#fff', border: 'none', borderRadius: 'var(--r-md)',
                padding: '8px 22px', fontSize: '13px',
                cursor: printing ? 'default' : 'pointer', fontWeight: 600,
              }}
            >
              {printing ? 'Printing…' : `Print ${totalLabels} label${totalLabels !== 1 ? 's' : ''}`}
            </button>
          )}
        </div>
    </ModalShell>
  );
}
